import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { sendVerificationEmail, sendResetPwEmail } from '../../../UtilsEmail';

import {
  trackEmailConfirmationClickResend,
  trackEmailConfirmationClickGoBack,
} from '../../../analytics';

import emailVerificationImg from '../../../assets/email-verification.png';
import passwordImg from '../../../assets/password.png';

import { Button } from '../../../components/general';
import { PageWrapper } from '../../../components/layout/content';
import { ErrorModal } from '../../../components/modals';

import { URL, RESEND_EMAIL_IN_SEC } from '../../../constants';

import { useUserStore } from '../../../stores/user';

import { getGALabel } from '../../../utils';

interface Props {
  type: 'verification' | 'reset-password';
}

const EmailVerification: React.FC<Props> = (props) => {
  const { type } = props;

  const navigate = useNavigate();

  const sessionEmail = sessionStorage.getItem('email') ?? '';

  const { user } = useUserStore();
  const email = user?.email ?? sessionEmail;

  const [isSending, setIsSending] = useState(false);
  const [resendMoment, setResendMoment] = useState(
    new Date(new Date().getTime() + RESEND_EMAIL_IN_SEC * 1000),
  );
  const [resendEmailIn, setResendEmailIn] = useState(RESEND_EMAIL_IN_SEC);

  const [isError, setIsError] = useState(false);

  let graphicImg = emailVerificationImg;
  let backLink = URL.ENTER_EMAIL;

  if (type === 'reset-password') {
    graphicImg = passwordImg;
    backLink = `${URL.LOGIN}?forgotPassword=true`;
  }

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (resendMoment) {
        const secondsLeft = countSecsFromNow(resendMoment);
        setResendEmailIn(secondsLeft <= 0 ? 0 : secondsLeft);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [resendMoment]);

  const countSecsFromNow = (to: Date) => {
    const now = new Date();
    const then = new Date(to);
    return Math.round((then.getTime() - now.getTime()) / 1000);
  };

  const resendEmail = async () => {
    setIsSending(true);

    trackEmailConfirmationClickResend(getGALabel(type));

    let isSuccess = false;

    if (type === 'verification') isSuccess = await sendVerificationEmail(email);
    else if (type === 'reset-password')
      isSuccess = await sendResetPwEmail(email);

    if (isSuccess)
      setResendMoment(
        new Date(new Date().getTime() + RESEND_EMAIL_IN_SEC * 1000),
      );
    else setIsError(true);

    setIsSending(false);
  };

  return (
    <PageWrapper
      type="onboarding"
      title="Check Your Inbox"
      className="items-center sm:justify-center"
    >
      <img
        className="fixed top-0 left-0 w-screen h-screen object-cover overflow-hidden -z-10"
        src="https://crowdclip-media-assets.s3.ap-southeast-2.amazonaws.com/onboarding/onboarding-background.png"
        alt="Background"
      />

      <div className="w-[var(--page-width)] sm:w-3/4 flex flex-col items-center justify-center p-6 sm:px-[10%] sm:py-16 gap-4 bg-black-overlay-600 text-center text-white rounded-md">
        <img
          className="w-44 aspect-square"
          src={graphicImg}
          alt="Email Confirmation Graphic"
        />

        <h4>Check your email</h4>

        <div>
          Click on the {type.replace(/-/g, ' ')} link sent to <b>{email}</b>. Be
          sure to check your spam if it doesn't arrive in your inbox!
        </div>

        <div className="flex flex-col items-center gap-2">
          <div>Still can't find the email?</div>

          <Button
            variant="primary-thin"
            className="w-44"
            onClick={resendEmail}
            disabled={resendEmailIn > 0}
            isSubmitBtn
            isProcessing={isSending}
          >
            Resend Email
          </Button>

          <div className="text-sm italic">
            {resendEmailIn > 0
              ? `You will be able to resend the email in ${resendEmailIn} seconds.`
              : 'You are able to resend the email now.'}
          </div>
        </div>

        <div>
          Entered incorrect email address?{' '}
          <Link to={backLink} onClick={trackEmailConfirmationClickGoBack}>
            Go back
          </Link>
        </div>
      </div>

      <ErrorModal
        show={isError}
        heading="Something went wrong"
        subheading="We couldn't resend the verification email. If you already clicked the link in a previous email, your account may be ready — try signing in. Otherwise, wait a moment and try again, or contact support."
        btn={
          <div className="w-full flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate(URL.LOGIN)}
              className="w-full sm:w-1/2"
            >
              Sign In
            </Button>

            <Button
              onClick={() => setIsError(false)}
              colour="pink"
              className="w-full sm:w-1/2"
            >
              Try Again
            </Button>
          </div>
        }
        isContactUs
      />
    </PageWrapper>
  );
};
export default EmailVerification;
